import { useEffect, useMemo, useRef } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebaseInstance';
import { getAppId } from '../firebaseConfig';

const appId = getAppId();

// Memoized validation function - only recalculates when relevant data changes
const validateUserChanges = (dataToSave) => {
  // Early exit if no data
  if (!dataToSave) return false;
  
  // Check avatar (exclude unsplash defaults)
  if (dataToSave.avatar?.trim() && !dataToSave.avatar.includes('unsplash.com')) {
    return true;
  }
  
  // Check media array (only if has items and at least one is not unsplash)
  if (Array.isArray(dataToSave.media) && dataToSave.media.length > 0) {
    for (const m of dataToSave.media) {
      if (m.url && !m.url.includes('unsplash.com')) {
        return true;
      }
    }
  }
  
  // Check showcases array
  if (Array.isArray(dataToSave.showcases) && dataToSave.showcases.length > 0) {
    for (const s of dataToSave.showcases) {
      if ((s.title && s.title.trim()) || (s.url && s.url.trim()) || (s.image && s.image.trim())) {
        return true;
      }
    }
  }
  
  // Check spotlight
  if (dataToSave.spotlight) {
    if ((dataToSave.spotlight.title && dataToSave.spotlight.title.trim()) ||
        (dataToSave.spotlight.url && dataToSave.spotlight.url.trim()) ||
        (dataToSave.spotlight.image && dataToSave.spotlight.image.trim())) {
      return true;
    }
  }
  
  // Check knowledge array
  if (Array.isArray(dataToSave.knowledge) && dataToSave.knowledge.length > 0) {
    for (const k of dataToSave.knowledge) {
      if (k && k.trim()) {
        return true;
      }
    }
  }
  
  // Check Q&A array
  if (Array.isArray(dataToSave.qna) && dataToSave.qna.length > 0) {
    for (const q of dataToSave.qna) {
      if ((q.question && q.question.trim()) || (q.answer && q.answer.trim())) {
        return true;
      }
    }
  }
  
  // Check links array
  if (Array.isArray(dataToSave.links) && dataToSave.links.length > 0) {
    for (const l of dataToSave.links) {
      if (l.url && l.url.trim()) {
        return true;
      }
    }
  }
  
  // Check contact info
  if (dataToSave.contactInfo) {
    if (Array.isArray(dataToSave.contactInfo.phones) && dataToSave.contactInfo.phones.length > 0) {
      for (const p of dataToSave.contactInfo.phones) {
        if (p.number && p.number.trim()) {
          return true;
        }
      }
    }
    if (Array.isArray(dataToSave.contactInfo.emails) && dataToSave.contactInfo.emails.length > 0) {
      for (const e of dataToSave.contactInfo.emails) {
        if (e.email && e.email.trim()) {
          return true;
        }
      }
    }
  }
  
  // Check simple string fields
  if (dataToSave.tagline?.trim() ||
      dataToSave.bookingUrl?.trim() ||
      dataToSave.profession?.trim() ||
      dataToSave.tone?.trim() ||
      dataToSave.userGoal?.trim()) {
    return true;
  }
  
  return false;
};

export const useAutoSave = (
  user,
  isLoaded,
  isLoadingFromFirestore,
  profileData,
  originalUsername,
  lastSavedQnaRef,
  lastSaveTimeRef,
  setIsSaving,
  setFieldSaveStatus,
  showToast
) => {
  // Cache validation results to avoid recalculating on every debounce
  const validationCacheRef = useRef({ 
    dataHash: null, 
    hasUserChanges: false, 
    isDefaultData: true 
  });
  
  // Track last saved data hash to detect actual changes
  const lastSavedDataHashRef = useRef(null);
  
  // Memoize the data to save (without username) - only recreate when profileData reference changes
  const dataToSaveMemo = useMemo(() => {
    if (!profileData) return null;
    const { username, ...rest } = profileData;
    return rest;
  }, [profileData]);
  
  // Memoize validation results - only recalculates when data actually changes
  // Uses a simple hash of relevant fields to detect changes
  const { hasUserChanges, isDefaultData } = useMemo(() => {
    if (!dataToSaveMemo) {
      return { hasUserChanges: false, isDefaultData: true };
    }
    
    // Create a lightweight hash of only the fields that affect validation
    // This avoids expensive JSON.stringify of entire arrays on every render
    const dataHash = [
      dataToSaveMemo.avatar,
      dataToSaveMemo.media?.length || 0,
      dataToSaveMemo.showcases?.length || 0,
      dataToSaveMemo.knowledge?.length || 0,
      dataToSaveMemo.qna?.length || 0,
      dataToSaveMemo.links?.length || 0,
      dataToSaveMemo.spotlight?.title,
      dataToSaveMemo.spotlight?.url,
      dataToSaveMemo.spotlight?.image,
      dataToSaveMemo.contactInfo?.phones?.length || 0,
      dataToSaveMemo.contactInfo?.emails?.length || 0,
      dataToSaveMemo.tagline,
      dataToSaveMemo.bookingUrl,
      dataToSaveMemo.profession,
      dataToSaveMemo.tone,
      dataToSaveMemo.userGoal,
      // Only stringify arrays if they exist (lazy evaluation)
      dataToSaveMemo.media?.length > 0 ? JSON.stringify(dataToSaveMemo.media) : '',
      dataToSaveMemo.showcases?.length > 0 ? JSON.stringify(dataToSaveMemo.showcases) : '',
      dataToSaveMemo.knowledge?.length > 0 ? JSON.stringify(dataToSaveMemo.knowledge) : '',
      dataToSaveMemo.qna?.length > 0 ? JSON.stringify(dataToSaveMemo.qna) : '',
      dataToSaveMemo.links?.length > 0 ? JSON.stringify(dataToSaveMemo.links) : '',
      dataToSaveMemo.contactInfo ? JSON.stringify(dataToSaveMemo.contactInfo) : ''
    ].join('|');
    
    // Return cached result if data hasn't changed
    if (validationCacheRef.current.dataHash === dataHash) {
      return {
        hasUserChanges: validationCacheRef.current.hasUserChanges,
        isDefaultData: validationCacheRef.current.isDefaultData
      };
    }
    
    // Recalculate validation only when data actually changed
    const hasChanges = validateUserChanges(dataToSaveMemo);
    const isDefault = !hasChanges &&
                     (!dataToSaveMemo.name || !dataToSaveMemo.name.trim()) && 
                     (!dataToSaveMemo.bio || !dataToSaveMemo.bio.trim());
    
    // Update cache
    validationCacheRef.current = {
      dataHash,
      hasUserChanges: hasChanges,
      isDefaultData: isDefault
    };
    
    return { hasUserChanges: hasChanges, isDefaultData: isDefault };
  }, [dataToSaveMemo]);
  
  useEffect(() => {
    if (!user || !isLoaded || isLoadingFromFirestore) {
      return;
    }

    const saveData = async () => {
      if (isLoadingFromFirestore) {
        return;
      }
      
      // EXCLUDE username from auto-save - username must be saved manually
      if (profileData.username && profileData.username !== originalUsername) {
        setIsSaving(false);
        return;
      }
      
      // Early exit if no data to save (using memoized result)
      if (!dataToSaveMemo || isDefaultData) {
        setIsSaving(false);
        return;
      }
      
      // EXCLUDE identity fields from auto-save - they use onBlur handlers
      const { name, tagline, bio, ...dataToSave } = dataToSaveMemo;
      
      // Create hash of current data to compare with last saved
      // This prevents showing "Saving changes..." when nothing actually changed
      const currentDataHash = JSON.stringify({
        qna: dataToSave.qna || [],
        knowledge: dataToSave.knowledge || [],
        media: dataToSave.media || [],
        links: dataToSave.links || [],
        showcases: dataToSave.showcases || [],
        chatPrompts: dataToSave.chatPrompts || [],
        avatar: dataToSave.avatar,
        spotlight: dataToSave.spotlight,
        contactInfo: dataToSave.contactInfo,
        bookingUrl: dataToSave.bookingUrl,
        profession: dataToSave.profession,
        tone: dataToSave.tone,
        userGoal: dataToSave.userGoal
      });
      
      // Only save if data actually changed
      if (lastSavedDataHashRef.current === currentDataHash) {
        setIsSaving(false);
        return;
      }
      
      // Data changed, proceed with save
      setIsSaving(true);
      try {
        if (isDefaultData) {
          setIsSaving(false);
          return;
        }
        
        // Track Q&A changes for notification
        const currentQnaHash = JSON.stringify(dataToSave.qna || []);
        const qnaChanged = lastSavedQnaRef.current !== null && lastSavedQnaRef.current !== currentQnaHash;
        
        // Explicitly set arrays to ensure deletions are saved properly
        const dataToSaveWithArrays = {
          ...dataToSave,
          qna: Array.isArray(profileData.qna) ? profileData.qna : [],
          knowledge: Array.isArray(profileData.knowledge) ? profileData.knowledge : [],
          media: Array.isArray(profileData.media) ? profileData.media : [],
          links: Array.isArray(profileData.links) ? profileData.links : [],
          showcases: Array.isArray(profileData.showcases) ? profileData.showcases : [],
          chatPrompts: Array.isArray(profileData.chatPrompts) ? profileData.chatPrompts : []
        };
        
        await setDoc(doc(db, 'artifacts', appId, 'users', user.uid), dataToSaveWithArrays, { merge: true });
        
        lastSavedQnaRef.current = currentQnaHash;
        lastSavedDataHashRef.current = currentDataHash; // Update saved hash after successful save
        lastSaveTimeRef.current = Date.now();
        
        if (qnaChanged && showToast) {
          const completedQna = (dataToSave.qna || []).filter(q => q.answer && q.answer.trim()).length;
          showToast(`Q&A pairs saved (${completedQna} completed)`, 'success');
        }
      } catch (err) {
        console.error("Error saving:", err);
        if (err.code === 'permission-denied') {
          if (showToast) {
            showToast('Failed to save profile. Please try again.', 'error');
          }
        } else {
          if (showToast) {
            showToast(`Failed to save: ${err.message || 'Unknown error'}`, 'error');
          }
        }
      } finally {
        setTimeout(() => setIsSaving(false), 500);
      }
    };

    const timeoutId = setTimeout(saveData, 1000);
    return () => clearTimeout(timeoutId);
  }, [dataToSaveMemo, isDefaultData, hasUserChanges, user, isLoaded, isLoadingFromFirestore, originalUsername, lastSavedQnaRef, lastSaveTimeRef, setIsSaving, setFieldSaveStatus, showToast]);
};

